import { type ReactNode, useEffect, useRef, useState } from "react";
import type { JSONContent } from "@tiptap/core";
import {
  ChevronDown,
  ChevronUp,
  ImagePlus,
  LoaderCircle,
  Sparkles,
  Trash2,
  TriangleAlert,
  Upload,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { StoryEditorSaveState } from "@/components/story/story-workspace-header";
import { LoreEditor } from "@/editors/rich-text/lore-editor";
import {
  createSideStoryDraft,
  sideStoryCanonStatusLabels,
  sideStoryCanonStatuses,
  type SideStoryCanonStatus,
  type SideStoryView,
} from "@/features/story";
import { cn } from "@/lib/utils";

const SAVE_DELAY_MS = 800;
const SIDE_STORY_HEADER_STORAGE_KEY_PREFIX = "story:side-story-header-collapsed:";
const EMPTY_PARAGRAPH: JSONContent = {
  type: "paragraph",
};

type SideStoryDraft = {
  body: JSONContent;
  canonStatus: SideStoryCanonStatus;
  summary: string;
};

function collectNodeText(node: JSONContent | null | undefined): string {
  if (!node) {
    return "";
  }

  if (typeof node.text === "string") {
    return node.text;
  }

  if (!Array.isArray(node.content)) {
    return "";
  }

  return node.content.map((child) => collectNodeText(child)).join("");
}

function normalizeTitleText(value: string | null | undefined) {
  return (value ?? "").replace(/\s+/g, " ").trim();
}

function buildTitleNode(title: string): JSONContent {
  return {
    attrs: {
      level: 1,
    },
    content: title
      ? [
          {
            text: title,
            type: "text",
          },
        ]
      : undefined,
    type: "heading",
  };
}

function ensureTitleInBody(body: JSONContent, title: string): JSONContent {
  const normalizedTitle = normalizeTitleText(title);
  const content = Array.isArray(body.content) ? [...body.content] : [];
  const firstNode = content[0];
  const firstNodeTitle = normalizeTitleText(collectNodeText(firstNode));

  if (!firstNode || firstNodeTitle.length === 0) {
    if (!normalizedTitle) {
      return body;
    }

    return {
      ...body,
      content: [buildTitleNode(normalizedTitle), EMPTY_PARAGRAPH, ...content],
    };
  }

  if (firstNodeTitle === normalizedTitle || normalizedTitle.length === 0) {
    return body;
  }

  return {
    ...body,
    content: [buildTitleNode(normalizedTitle), ...content],
  };
}

function extractTitleFromBody(body: JSONContent, fallbackTitle: string) {
  const content = Array.isArray(body.content) ? body.content : [];

  for (const node of content) {
    const candidate = normalizeTitleText(collectNodeText(node));

    if (candidate.length > 0) {
      return candidate;
    }
  }

  return normalizeTitleText(fallbackTitle);
}

function readCollapsedState(sideStoryId: string) {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.localStorage.getItem(
      `${SIDE_STORY_HEADER_STORAGE_KEY_PREFIX}${sideStoryId}`,
    ) === "true"
  );
}

function buildDraft(story: SideStoryView | null): SideStoryDraft {
  return {
    body: ensureTitleInBody(
      story?.body ?? createSideStoryDraft().body,
      story?.title ?? "",
    ),
    canonStatus: story?.canonStatus ?? "draft",
    summary: story?.summary ?? "",
  };
}

function serializeDraft(draft: SideStoryDraft) {
  return JSON.stringify(draft);
}

type SideStoryFormProps = {
  mode: "create" | "edit";
  headerActions?: ReactNode;
  onCancelCreate?: () => void;
  onDelete?: () => void;
  onGenerateImage?: () => Promise<void>;
  onSave: (input: {
    body: JSONContent;
    canonStatus: SideStoryCanonStatus;
    summary: string;
    title: string;
  }) => Promise<void>;
  onSaveStateChange?: (
    state: StoryEditorSaveState,
    error: string | null,
  ) => void;
  onUploadImage?: (file: File) => Promise<void>;
  story: SideStoryView | null;
};

export function SideStoryForm({
  mode,
  headerActions,
  onCancelCreate,
  onDelete,
  onGenerateImage,
  onSave,
  onSaveStateChange,
  onUploadImage,
  story,
}: SideStoryFormProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(() =>
    readCollapsedState(story?.id ?? "draft-side-story"),
  );
  const [draft, setDraft] = useState<SideStoryDraft>(() => buildDraft(story));
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState(() =>
    serializeDraft(buildDraft(story)),
  );
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<StoryEditorSaveState>("idle");
  const onSaveRef = useRef(onSave);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const draftSnapshot = serializeDraft(draft);
  const documentTitle = extractTitleFromBody(
    draft.body,
    story?.title ?? "",
  );
  const firstAttachment = story?.attachments[0] ?? null;
  const imageCount = story?.attachments.length ?? 0;
  const toggleLabel = collapsed
    ? t("story.sideStories.expandHeader", {
        defaultValue: "Expand side story header",
      })
    : t("story.sideStories.collapseHeader", {
        defaultValue: "Collapse side story header",
      });
  const statusContent =
    saveState === "saving" ? (
      <>
        <LoaderCircle className="h-4 w-4 animate-spin" />
        {t("story.save.saving")}
      </>
    ) : saveState === "error" ? (
      <>
        <TriangleAlert className="h-4 w-4" />
        {t("story.save.error")}
      </>
    ) : null;

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    const nextDraft = buildDraft(story);
    setCollapsed(readCollapsedState(story?.id ?? "draft-side-story"));
    setDraft(nextDraft);
    setLastSavedSnapshot(serializeDraft(nextDraft));
    setLightboxOpen(false);
    setSaveError(null);
    setSaveState("idle");
  }, [mode, story]);

  useEffect(() => {
    onSaveStateChange?.(saveState, saveError);
  }, [onSaveStateChange, saveError, saveState]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      `${SIDE_STORY_HEADER_STORAGE_KEY_PREFIX}${story?.id ?? "draft-side-story"}`,
      String(collapsed),
    );
  }, [collapsed, story?.id]);

  useEffect(() => {
    if (mode !== "edit" || !story) {
      return;
    }

    const title = extractTitleFromBody(draft.body, story.title);
    const normalizedStoryBody = ensureTitleInBody(story.body, story.title);

    if (
      draftSnapshot === lastSavedSnapshot ||
      (
        JSON.stringify(draft.body) === JSON.stringify(normalizedStoryBody) &&
        draft.summary === story.summary &&
        draft.canonStatus === story.canonStatus &&
        title === story.title
      )
    ) {
      return;
    }

    setSaveState("saving");
    const timeout = window.setTimeout(() => {
      void (async () => {
        try {
          await onSaveRef.current({
            body: draft.body,
            canonStatus: draft.canonStatus,
            summary: draft.summary,
            title,
          });
          setLastSavedSnapshot(draftSnapshot);
          setSaveError(null);
          setSaveState("saved");
        } catch (error) {
          setSaveError(
            error instanceof Error
              ? error.message
              : t("story.messages.saveError"),
          );
          setSaveState("error");
        }
      })();
    }, SAVE_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [draft, draftSnapshot, lastSavedSnapshot, mode, story, t]);

  async function handleCreate() {
    setSaveState("saving");
    try {
      await onSaveRef.current({
        body: draft.body,
        canonStatus: draft.canonStatus,
        summary: draft.summary,
        title: documentTitle,
      });
      setSaveError(null);
      setSaveState("saved");
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : t("story.messages.saveError"),
      );
      setSaveState("error");
    }
  }

  async function handleUploadImage(file: File | null) {
    if (!file || !onUploadImage) {
      return;
    }

    setSaveState("saving");

    try {
      await onUploadImage(file);
      setSaveError(null);
      setSaveState("saved");
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : t("story.messages.saveError"),
      );
      setSaveState("error");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  async function handleGenerateImage() {
    if (!onGenerateImage) {
      return;
    }

    setSaveState("saving");

    try {
      await onGenerateImage();
      setSaveError(null);
      setSaveState("saved");
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : t("story.messages.saveError"),
      );
      setSaveState("error");
    }
  }

  const canCreate =
    documentTitle.trim().length > 0 && draft.summary.trim().length > 0;

  return (
    <section className={collapsed ? "space-y-3" : "space-y-6"}>
      <header
        className={
          collapsed
            ? "space-y-3 px-1 transition-all duration-300 ease-out"
            : "space-y-4 px-1 transition-all duration-300 ease-out"
        }
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={
                  collapsed
                    ? "rounded-full border-clay/15 bg-white/70 px-2.5 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] text-clay transition-all duration-300 ease-out"
                    : "rounded-full border-clay/15 bg-white/70 px-3 py-1 text-[0.7rem] uppercase tracking-[0.18em] text-clay transition-all duration-300 ease-out"
                }
              >
                {t("story.modes.continuous")}
              </Badge>
              <Badge
                variant="outline"
                className={
                  collapsed
                    ? "rounded-full border-clay/15 bg-white/70 px-2.5 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] text-clay transition-all duration-300 ease-out"
                    : "rounded-full border-clay/15 bg-white/70 px-3 py-1 text-[0.7rem] uppercase tracking-[0.18em] text-clay transition-all duration-300 ease-out"
                }
              >
                {t(sideStoryCanonStatusLabels[draft.canonStatus])}
              </Badge>
            </div>

            <h2
              className={
                collapsed
                  ? "truncate border-b border-ink/10 pb-1 font-display text-[clamp(1.55rem,2.2vw,2rem)] leading-none text-foreground"
                  : "border-b border-ink/10 pb-3 font-display text-[clamp(2.15rem,4vw,3.5rem)] leading-[0.95] text-foreground"
              }
            >
              {documentTitle ||
                t("story.sideStories.untitled", {
                  defaultValue: "Untitled side story",
                })}
            </h2>
          </div>

          <div className="flex shrink-0 flex-col items-end gap-3">
            <Button
              aria-label={toggleLabel}
              className="h-8 w-8 rounded-full border border-[#dcccb7] bg-white p-0 text-muted-foreground shadow-none transition-all duration-300 ease-out hover:bg-[#f8f3eb] hover:text-foreground"
              onClick={() => setCollapsed((current) => !current)}
              type="button"
              variant="ghost"
            >
              <span className="transition-transform duration-300 ease-out">
                {collapsed ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </span>
            </Button>

            {statusContent ? (
              <p
                aria-live="polite"
                className="inline-flex items-center gap-2 text-sm whitespace-nowrap text-muted-foreground transition-all duration-300 ease-out"
              >
                {statusContent}
              </p>
            ) : null}

            {collapsed && firstAttachment?.imageUrl ? (
              <button
                aria-label={t("story.attachments.sideStoryImageAlt", {
                  title: documentTitle || story?.title || "",
                  defaultValue: "Open side story illustration",
                })}
                className="block w-full max-w-[8.5rem] overflow-hidden rounded-[16px] border border-border/60 bg-white shadow-sm transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setLightboxOpen(true)}
                type="button"
              >
                <img
                  alt={t("story.attachments.sideStoryImageAlt", {
                    title: documentTitle || story?.title || "",
                    defaultValue: "Side story illustration",
                  })}
                  className="h-24 w-full object-cover transition-all duration-300 ease-out hover:scale-[1.02]"
                  src={firstAttachment.imageUrl}
                />
              </button>
            ) : null}
          </div>
        </div>

        {saveError ? (
          <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {saveError}
          </p>
        ) : null}
      </header>

      <div
        className={
          collapsed
            ? "pointer-events-none max-h-0 overflow-hidden border-t border-ink/10 pt-0 opacity-0 -translate-y-2 transition-all duration-300 ease-out"
            : "max-h-[34rem] overflow-hidden border-t border-ink/10 pt-6 opacity-100 translate-y-0 transition-all duration-300 ease-out"
        }
      >
        <input
          ref={fileInputRef}
          accept="image/*"
          className="hidden"
          data-testid="side-story-image-input"
          onChange={(event) => {
            void handleUploadImage(event.target.files?.[0] ?? null);
          }}
          type="file"
        />

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_12rem]">
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-[13rem_minmax(0,1fr)] md:items-start">
              <div className="space-y-2">
                <Label htmlFor="side-story-canon-status">
                  {t("story.sideStories.fields.status", {
                    defaultValue: "Status",
                  })}
                </Label>
                <Select
                  onValueChange={(value) =>
                    setDraft((current) => ({
                      ...current,
                      canonStatus: value as SideStoryCanonStatus,
                    }))
                  }
                  value={draft.canonStatus}
                >
                  <SelectTrigger
                    aria-label={t("story.sideStories.fields.status", {
                      defaultValue: "Status",
                    })}
                    id="side-story-canon-status"
                  >
                    <SelectValue
                      placeholder={t("story.sideStories.fields.status", {
                        defaultValue: "Status",
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {sideStoryCanonStatuses.map((nextStatus) => (
                      <SelectItem key={nextStatus} value={nextStatus}>
                        {t(`lore.statuses.${nextStatus}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-end justify-start gap-2 md:justify-end">
                {headerActions ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {headerActions}
                  </div>
                ) : null}
                <Button
                  disabled={!onGenerateImage}
                  onClick={() => {
                    void handleGenerateImage();
                  }}
                  type="button"
                  variant="secondary"
                >
                  <Sparkles className="h-4 w-4" />
                  {t("story.attachments.generateIllustration")}
                </Button>
                {onUploadImage ? (
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    type="button"
                    variant="outline"
                  >
                    {firstAttachment ? (
                      <Upload className="h-4 w-4" />
                    ) : (
                      <ImagePlus className="h-4 w-4" />
                    )}
                    {firstAttachment
                      ? t("story.attachments.replaceImage")
                      : t("story.attachments.uploadImage")}
                  </Button>
                ) : null}
                {mode === "edit" && onDelete ? (
                  <Button onClick={onDelete} type="button" variant="ghost">
                    <Trash2 className="h-4 w-4" />
                    {t("story.sideStories.deleteAction")}
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="space-y-3">
              <Label htmlFor="side-story-summary">
                {t("story.sideStories.fields.summary")}
              </Label>
              <textarea
                className="min-h-32 w-full rounded-[22px] border border-input bg-white px-4 py-3 text-sm leading-6 text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                id="side-story-summary"
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    summary: event.target.value,
                  }))
                }
                placeholder={t("story.sideStories.fields.summaryPlaceholder")}
                value={draft.summary}
              />
              <p className="text-xs text-muted-foreground">
                {t("story.sideStories.summaryHint")}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div
              className={cn(
                "flex min-h-[14rem] items-center justify-center rounded-[2rem] border border-dashed border-ink/25 bg-white/55 p-4 text-center text-muted-foreground",
                firstAttachment?.imageUrl && "overflow-hidden border-solid bg-white p-0",
              )}
            >
              {firstAttachment?.imageUrl ? (
                <button
                  className="h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => setLightboxOpen(true)}
                  type="button"
                >
                  <img
                    alt={firstAttachment.fileName}
                    className="h-full w-full object-cover"
                    src={firstAttachment.imageUrl}
                  />
                </button>
              ) : (
                <p>
                  {t("story.attachments.emptySideStoryImages")}
                </p>
              )}
            </div>
            {imageCount > 1 ? (
              <p className="text-xs text-muted-foreground">
                {t("story.sideStories.attachmentCount", {
                  count: imageCount,
                  defaultValue:
                    "{{count}} illustrations are currently attached to this side story.",
                })}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <LoreEditor
        autofocus={mode === "create"}
        content={draft.body}
        contentClassName="lore-editor-page-breaks"
        onChange={(body) =>
          setDraft((current) => ({
            ...current,
            body,
          }))
        }
        paginated={false}
        placeholder={t("story.sideStories.bodyPlaceholder")}
      />

      <div className="flex flex-wrap justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {mode === "create" && onCancelCreate ? (
            <Button onClick={onCancelCreate} type="button" variant="ghost">
              {t("common.cancel")}
            </Button>
          ) : null}
        </div>
        {mode === "create" ? (
          <div className="space-y-2 text-right">
            <Button
              disabled={!canCreate}
              onClick={() => void handleCreate()}
              type="button"
            >
              {t("story.sideStories.createAction")}
            </Button>
            {!canCreate ? (
              <p className="text-sm text-muted-foreground">
                {t("story.sideStories.createHint", {
                  defaultValue:
                    "Add a title as the first line of the story and write a short summary before creating it.",
                })}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="rounded-full bg-secondary px-4 py-2 text-sm text-secondary-foreground">
            {t("story.sideStories.autosaveHint")}
          </div>
        )}
      </div>

      {firstAttachment?.imageUrl ? (
        <ImageLightbox
          alt={firstAttachment.fileName}
          imageUrl={firstAttachment.imageUrl}
          onOpenChange={setLightboxOpen}
          open={lightboxOpen}
          title={firstAttachment.fileName}
        />
      ) : null}
    </section>
  );
}
